import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import Modal from './Modal.tsx';
import { api, errorMessage } from '../lib/api.ts';
import type { DeckSummaryPayload, LibraryItem, LibraryPayload } from '../../shared/api.ts';
import type { DeckSection } from '../../shared/deck.ts';
import { isDivider } from '../lib/slideBlocks.ts';

/** All this modal reads off a slide. Deliberately the INTERSECTION of the two
 *  sources rather than either wire shape: the caller passes `SlideEntry`s from
 *  the editor, the whole-deck path fetches its own from `_summary`, and the
 *  picker needs only these three fields from either. */
type SaveableSlide = { id: string; title: string; section: string };

/** What the modal saves: append to an existing item, or start a new one. */
export type SaveToLibraryPayload =
  /** Extend an existing item — its deck slug. */
  | { slideIds: string[]; itemSlug: string; name?: never; category?: never }
  /** Make a new item, named and filed. */
  | { slideIds: string[]; name: string; category: string; itemSlug?: never };

type SaveToLibraryModalProps = {
  /** The slides to save. Absent means "the whole deck", which the modal then
   *  fetches for itself. */
  slides?: SaveableSlide[] | null;
  sections?: DeckSection[];
  slug: string;
  deckTitle?: string;
  onCancel: () => void;
  onSave: (payload: SaveToLibraryPayload) => void;
  busy?: boolean;
};

/**
 * "Save to RKH Library" — the library is a shelf of ITEMS (one slide, or an
 * ordered series). Default path: the selection becomes a NEW item, filed under
 * a category you pick (existing label or a new one). Alternative: append it to
 * an existing item, extending a series (e.g. adding a results slide to a case
 * study).
 *
 * A selection spanning several deck sections keeps that structure — the server
 * gives the item one internal section per source section — so the summary here
 * tells the user exactly what will land.
 *
 * Two ways to say what to save: `slides` (an explicit selection, from the deck
 * editor) or `slug` alone (the whole deck, from the dashboard — fetched here so
 * the caller doesn't need the slide list).
 */
export default function SaveToLibraryModal({
  slides,
  sections,
  slug,
  deckTitle,
  onCancel,
  onSave,
  busy,
}: SaveToLibraryModalProps) {
  // null = still loading.
  const [data, setData] = useState<{ items: LibraryItem[]; categories: string[] } | null>(null);
  const [deckSlides, setDeckSlides] = useState<SaveableSlide[] | null>(slides || null);
  const [deckSections, setDeckSections] = useState<DeckSection[]>(sections || []);
  const [deckError, setDeckError] = useState<string | null>(null);
  const [mode, setMode] = useState<'new' | 'append'>('new');
  const [category, setCategory] = useState(''); // selected existing label or '' for the typed one
  const [newCategory, setNewCategory] = useState('');
  const [appendTo, setAppendTo] = useState('');
  const [name, setName] = useState('');
  const newCatRef = useRef<HTMLInputElement>(null);
  const nameTouched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<LibraryPayload>('/api/library')
      .then((d) => {
        if (cancelled) return;
        const items = d.items || [];
        // Items with no category are Uncategorised — they carry no label, so
        // they contribute nothing to the list rather than an empty entry.
        const categories = [...new Set(items.map((it) => it.category).filter((c): c is string => !!c))];
        setData({ items, categories });
        setCategory(categories[0] || '');
        setAppendTo(items[0]?.slug || '');
      })
      .catch(() => {
        if (!cancelled) setData({ items: [], categories: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Dashboard path: no explicit selection, so take the whole deck.
  useEffect(() => {
    if (slides || !slug) return;
    let cancelled = false;
    api
      .get<DeckSummaryPayload>(`/api/slides/${slug}/_summary`)
      .then((d) => {
        if (cancelled) return;
        setDeckSlides(d.slides || []);
        setDeckSections(d.sections || []);
      })
      .catch((err) => {
        if (!cancelled) setDeckError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [slug, slides]);

  // Dividers are structural filler the library refuses — never count them.
  const picked = useMemo(() => (deckSlides || []).filter((s) => !isDivider(s)), [deckSlides]);

  // One entry per source section, in deck order — the item's internal shape.
  const parts = useMemo(() => {
    const named = new Map((deckSections || []).map((s) => [s.key, s.name || s.key]));
    const out = [];
    const seen = new Map();
    for (const s of picked) {
      if (!seen.has(s.section)) {
        const part = { key: s.section, name: named.get(s.section) || s.section, count: 0 };
        seen.set(s.section, part);
        out.push(part);
      }
      seen.get(s.section).count += 1;
    }
    return out;
  }, [picked, deckSections]);

  // Suggested item name: a single slide names itself, a wider selection takes
  // the deck's title. Stops suggesting the moment the user types.
  useEffect(() => {
    if (nameTouched.current || picked.length === 0) return;
    setName(picked.length === 1 ? (picked[0]?.title ?? '') : deckTitle || slug || '');
  }, [picked, deckTitle, slug]);

  const loading = data === null || deckSlides === null;
  const resolvedCategory = category === '__new' ? newCategory.trim() : category;
  const canSave =
    !busy &&
    !loading &&
    picked.length > 0 &&
    (mode === 'new'
      ? name.trim().length > 0 && (resolvedCategory.length > 0 || data.categories.length === 0)
      : !!appendTo);

  function submit(e: FormEvent) {
    e?.preventDefault();
    if (!canSave) return;
    const slideIds = picked.map((s) => s.id);
    if (mode === 'append') onSave({ slideIds, itemSlug: appendTo });
    else onSave({ slideIds, name: name.trim(), category: resolvedCategory || '' });
  }

  const one = picked.length === 1;
  const appendItem = data?.items.find((it) => it.slug === appendTo) || null;

  return (
    <Modal
      /* Fixed short heading: the subject is a slide title, which can be a
         whole sentence — it lives in the summary line below, where it can
         wrap, instead of blowing up a 1.35rem heading. */
      title="Save to the RKH Library"
      onClose={onCancel}
      className="modal--confirm"
      panelAs="form"
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={!canSave}>
            {picked.length > 1 ? `Save ${picked.length} slides` : 'Save to library'}
          </button>
        </>
      }
    >
      <>
        {deckError && <p className="small error">{deckError}</p>}
        {loading && !deckError && <p className="muted small">Loading…</p>}
        {!loading && picked.length === 0 && (
          <p className="muted small">Nothing to save: section dividers can't go into the library.</p>
        )}
        {!loading && picked.length > 0 && (
          <>
            <p className="small save-library__summary">
              <strong>
                {picked.length} slide{one ? '' : 's'}
              </strong>
              {one ? (
                <span className="muted">: “{picked[0]?.title || 'Untitled'}”</span>
              ) : (
                parts.length > 1 && (
                  <>
                    {' '}
                    across {parts.length} sections
                    <span className="muted">: {parts.map((p) => p.name).join(', ')}</span>
                  </>
                )
              )}
            </p>
            {/* Each option is a BLOCK, not a row: at the modal's 420px a
                  sentence-length label and a <select> on one line squeeze the
                  select to ~110px and clip its longest option. Label on top,
                  controls full-width beneath. */}
            <div className="save-library__choices" role="radiogroup" aria-label="How to save">
              <label className="save-library__opt">
                <span className="save-library__opt-head">
                  <input
                    type="radio"
                    name="save-mode"
                    checked={mode === 'new'}
                    onChange={() => setMode('new')}
                    disabled={busy}
                  />
                  <span>New item</span>
                </span>
                <span className="save-library__fields">
                  <span className="save-library__field">
                    <span>Category</span>
                    <select
                      className="save-library__new"
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        setMode('new');
                        if (e.target.value === '__new') setTimeout(() => newCatRef.current?.focus(), 0);
                      }}
                      disabled={busy}
                    >
                      {data.categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                      <option value="__new">New category…</option>
                    </select>
                  </span>
                  {category === '__new' && (
                    <span className="save-library__field">
                      <span />
                      <input
                        ref={newCatRef}
                        type="text"
                        className="save-library__new"
                        value={newCategory}
                        placeholder="e.g. Case studies"
                        onChange={(e) => setNewCategory(e.target.value)}
                        disabled={busy}
                      />
                    </span>
                  )}
                  <span className="save-library__field">
                    <span>Called</span>
                    <input
                      type="text"
                      className="save-library__new"
                      value={name}
                      placeholder="Item name"
                      maxLength={80}
                      onChange={(e) => {
                        nameTouched.current = true;
                        setName(e.target.value);
                        setMode('new'); // typing a name means you want a new item
                      }}
                      disabled={busy}
                    />
                  </span>
                </span>
              </label>
              {data.items.length > 0 && (
                <label className="save-library__opt">
                  <span className="save-library__opt-head">
                    <input
                      type="radio"
                      name="save-mode"
                      checked={mode === 'append'}
                      onChange={() => setMode('append')}
                      disabled={busy}
                    />
                    <span>
                      Add to an existing item <span className="muted">(extends the series)</span>
                    </span>
                  </span>
                  <span className="save-library__fields">
                    {/* Options carry the NAME only. A native select clips its
                          closed display, and appending the count + category made
                          every real item name too wide for the row — that detail
                          reads better as the hint below. */}
                    <select
                      className="save-library__new"
                      value={appendTo}
                      onChange={(e) => {
                        setAppendTo(e.target.value);
                        setMode('append');
                      }}
                      disabled={busy}
                      aria-label="Library item to extend"
                    >
                      {data.items.map((it) => (
                        <option key={it.slug} value={it.slug}>
                          {it.name}
                        </option>
                      ))}
                    </select>
                    {appendItem && (
                      <span className="muted small">
                        {appendItem.count} slide{appendItem.count === 1 ? '' : 's'} · {appendItem.category}
                      </span>
                    )}
                  </span>
                </label>
              )}
            </div>
          </>
        )}
        <p className="muted small">
          The save is a snapshot: later edits to this deck don't change the library copy.
          {parts.length > 1 && mode === 'new'
            ? ' The deck sections are kept inside the item, so inserting it back into a deck restores them.'
            : parts.length > 1
              ? ' Appending puts every slide into one section of that item; pick "New item" to keep the sections.'
              : ' Rename it any time on the Library page.'}
        </p>
      </>
    </Modal>
  );
}
